import React, { useCallback, useEffect, useState } from 'react';
import { generateImgproxyUrl } from '../../services/imgproxy';
import { useDutyFree } from '../../context/DutyFreeContext';
import HeroPhoto from './HeroPhoto';
import GlassInfoCard from './GlassInfoCard';
import { useHeroPhotoAnalysis } from './hooks/useHeroPhotoAnalysis';
import { pickHeroImageSource } from './utils/imageSource';
import './NewModelLanding.css';

/**
 * Imgproxy target dimensions for the hero. Wide 4:3 keeps the car
 * dominant on the upper half of the viewport while keeping CDN traffic
 * moderate (DPR-aware downstream).
 */
const HERO_IMG_WIDTH = 800;
const HERO_IMG_HEIGHT = 600;

const formatPrice = (price) => {
  if (!price || price === 0) return 'Contact';
  return `€${price.toLocaleString()}`;
};

/**
 * Builds the launch-context payload for the chat widget.
 * `workflow: 'showroom'` is forward-compatible — backend routing
 * for the showroom workflow is added in a follow-up task.
 *
 * #1362: `metadata.language` is NOT set here any more. This used to carry the
 * PAGE language (`useTranslation().language`, i.e. the customer's choice or the
 * `fr` default), which reported a customer who never touched the selector as
 * having chosen French and so pre-empted the agent's own tenant default. The
 * language is now added by the transport (`useTextChat`), and only when a real
 * choice exists — see `i18n/selectedLanguage.js`.
 */
const buildLaunchContext = (car, displayTitle, displayPrice) => ({
  action: 'ask_about_car',
  workflow: 'showroom',
  metadata: {
    carId: car?.id,
    carTitle: displayTitle,
    price: displayPrice,
    make: car?.make,
    model: car?.model,
    year: car?.year,
  },
});

const openWidget = (launchContext) => {
  if (window.ShiftGearsWidget?.open) {
    window.ShiftGearsWidget.open('chat', launchContext);
    return;
  }
  window.dispatchEvent(
    new CustomEvent('shiftgears:open', { detail: { mode: 'chat', launchContext } }),
  );
};

/**
 * Full-viewport new-model landing for the Autodis OMODA/JAECOO showroom
 * (issue #884). Self-contained: owns image resolution, canvas-based
 * adaptive contrast, and CTA handling. Composed of HeroPhoto + photo-fade
 * overlay + GlassInfoCard.
 *
 * @param {object} props
 * @param {object} props.car — Car object (DealerCarDto, must have `isNewModel: true`).
 * @param {string} props.title — pre-computed display title.
 */
const NewModelLanding = ({ car, title }) => {
  const { isDutyFree, calculateDutyFree } = useDutyFree();
  const [mainImage, setMainImage] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);

  const sourceImagePath = pickHeroImageSource(car);

  // Resolve imgproxy URL with isMounted guard and per-source reset.
  useEffect(() => {
    setMainImage(null);
    setImgFailed(false);
    if (!sourceImagePath) return undefined;
    let isMounted = true;
    generateImgproxyUrl(sourceImagePath, HERO_IMG_WIDTH, HERO_IMG_HEIGHT).then((url) => {
      if (isMounted) setMainImage(url);
    });
    return () => { isMounted = false; };
  }, [sourceImagePath]);

  // Hero photo analysis runs only when imgproxy URL is final, to avoid
  // double decode and FOUC of contrast values. Hook handles cancellation.
  const { bgIsDark, fadeColor, midColor } = useHeroPhotoAnalysis(mainImage);

  const displayPrice = car && isDutyFree ? calculateDutyFree(car) : car?.price;
  const imageUrl = mainImage || sourceImagePath;

  const handleOpenWidget = () => {
    try {
      openWidget(buildLaunchContext(car, title, displayPrice));
    } catch (err) {
      console.error('[NewModelLanding] Error opening widget:', err);
    }
  };

  // Stable handler so React.memo on HeroPhoto can short-circuit
  // re-renders triggered by parent state unrelated to the image.
  const handleImgError = useCallback(() => setImgFailed(true), []);

  return (
    <div
      className={`landing landing--new-model${bgIsDark ? ' landing--dark-bg' : ''}`}
      style={{
        '--landing-fade-color': fadeColor,
        '--landing-fade-mid': midColor,
      }}
    >
      <div className="landing__container">
        <HeroPhoto
          car={car}
          imageUrl={imageUrl}
          imgFailed={imgFailed}
          onImgError={handleImgError}
          title={title}
        />

        {/* Gradient overlay — bridge between photo and glass card.
            Sibling of HeroPhoto so it stays anchored to viewport bottom
            regardless of photo height. */}
        <div className="landing__photo-fade" aria-hidden="true" />

        <GlassInfoCard
          car={car}
          title={title}
          displayPrice={displayPrice}
          formatPrice={formatPrice}
          onCta={handleOpenWidget}
        />
      </div>
    </div>
  );
};

export default NewModelLanding;
